import useFetchApi, { FetchingStatus } from 'hooks/useFetchApi/useFetchApi'
import { useCallback, useEffect, useMemo, useState } from 'react'
import giftCardService from 'Services/api/giftCard.service'
import { GiftCardCartItem } from './types'
import { v4 as uuidv4 } from 'uuid'
import {
    GiftCardSaleProcessContract,
    GiftCardSaleType,
    GiftCardTypePOSContract,
    ReceivePaymentContract,
} from 'models'
import { OrderFulfilmentType } from 'store/models/sellModels'
import { OrderChannel } from 'constant/OrderChannels'
import paymentService from 'Services/api/payment.service'
import Decimal from 'decimal.js'
import usePinDialog from 'components/PinPanel/usePinDialog'
import { usePosContext } from 'views/Sale/contexts/PosProvider'
import { useCustomerSellPopupContext } from 'components/Sell/CustomerSellPopup/CustomerSellPopupContext'
import { ProcessingSaleCallback } from 'components/Sell/payment/Models/ProcessingSaleCallback'
import salesService from 'Services/api/sales.service'
import { OrderDetailModel } from 'components/Sell/payment/Models/OrderDetailModels'
import calculateTax from 'views/Sale/utils/orderCalculation/taxCalculation'
import { useQuickServiceSaleContext } from 'views/Sale/QuickService/contexts/QuickServiceSaleContext'
import Guid from 'utilities/Guid'

interface UseBuyGiftCardParams {
    isDialogOpened: boolean
}

export const MOCK_GIFT_CARD_ORDER_ID = 'Mock'

const isExceedStock = (
    price: number,
    purchaseQuantity: number,
    data: GiftCardTypePOSContract | undefined,
) => {
    if (!data) return false

    const matchInventory = data.inventories?.find(
        (i) => i.giftCardPrice === price,
    )
    if (!matchInventory) return false

    return matchInventory.onHandQuantity! < purchaseQuantity
}

function useBuyGiftCard({ isDialogOpened }: UseBuyGiftCardParams) {
    const { taxes } = usePosContext()
    const {
        popupDefaultCustomerId: defaultCustomerID,
    } = useCustomerSellPopupContext()
    const { pinCode } = usePinDialog()

    const { orderCustomerID } = useQuickServiceSaleContext()

    const [
        giftCardProducts,
        getGiftCardProducts,
        getGiftCardProductsStatus,
    ] = useFetchApi(giftCardService.getGiftCardPOSProducts)

    // #region States
    const [ownerCustomerID, setOwnerCustomerID] = useState<string | undefined>(
        undefined,
    )
    const [cartItems, setCartItems] = useState<GiftCardCartItem[]>([])
    // #endregion

    const onSelectCustomer = useCallback((customerID?: string) => {
        setOwnerCustomerID(customerID)
    }, [])

    const addNewItem = useCallback(
        (giftCardTypeID: string, initialPrice: number) => {
            const existingCartItem = cartItems.find(
                (e) =>
                    e.giftCardTypeID === giftCardTypeID &&
                    e.initialPrice === initialPrice,
            )
            const giftCardType = giftCardProducts?.find(
                (g) => g.giftCardTypeID === giftCardTypeID,
            )
            const tax = taxes.find((a) => a.taxID === giftCardType?.taxID)

            if (existingCartItem) {
                setCartItems((prev) =>
                    prev.map((e) => {
                        if (e === existingCartItem) {
                            const newQuantity = e.quantity + 1
                            const isExceedStockCount = isExceedStock(
                                initialPrice,
                                newQuantity,
                                giftCardType,
                            )
                            return {
                                ...e,
                                quantity: newQuantity,
                                isExceedStockCount,
                            }
                        }

                        return e
                    }),
                )
            } else {
                const isExceedStockCount = isExceedStock(
                    initialPrice,
                    1,
                    giftCardType,
                )
                setCartItems((prev) => [
                    ...prev,
                    {
                        cartItemID: uuidv4(),
                        giftCardTypeID,
                        initialPrice,
                        quantity: 1,
                        isExceedStockCount,
                        taxInclusive: giftCardType?.isTaxInclusive ?? false,
                        taxRate: tax?.rate ?? 0,
                    },
                ])
            }
        },
        [cartItems, giftCardProducts, taxes],
    )

    const increaseQuantity = useCallback((cartItemID: string) => {
        setCartItems((prev) =>
            prev.map((e) => {
                if (e.cartItemID === cartItemID)
                    return { ...e, quantity: e.quantity + 1 }
                return e
            }),
        )
    }, [])

    const removeItem = useCallback((cartItemID: string) => {
        setCartItems((prev) => prev.filter((e) => e.cartItemID !== cartItemID))
    }, [])

    const { totalPrice, totalTax, taxExclusive, taxInclusive } = useMemo(() => {
        let totalPrice = new Decimal(0)

        let taxExclusive = new Decimal(0)
        let taxInclusive = new Decimal(0)
        cartItems.forEach((cur) => {
            let subTotal = new Decimal(cur.initialPrice).mul(cur.quantity)
            totalPrice = totalPrice.plus(subTotal)
            if (cur.taxInclusive)
                taxInclusive = taxInclusive.plus(
                    calculateTax(subTotal, cur.taxRate, true),
                )
            else
                taxExclusive = taxExclusive.plus(
                    calculateTax(subTotal, cur.taxRate, false),
                )
        })
        totalPrice = totalPrice.plus(taxExclusive)
        return {
            totalPrice,
            totalTax: taxExclusive.plus(taxInclusive),
            taxExclusive,
            taxInclusive,
        }
    }, [cartItems])
    const [giftCardSaleID, setGiftCardSaleID] = useState('')

    const handleOnProcessingSale: ProcessingSaleCallback = async (
        payment: ReceivePaymentContract,
        _customerID: string,
        currencyID: number,
        _isOffline: boolean | undefined,
        pointRedeem: number,
        _orderID: string,
    ) => {
        const payload: GiftCardSaleProcessContract = {
            type: GiftCardSaleType.Purchase,
            giftCardItems: cartItems.map((item) => ({
                giftCardTypeID: item.giftCardTypeID,
                price: item.initialPrice,
                quantity: item.quantity,
            })),
            orderFulfillmentTypeID: OrderFulfilmentType.DINE_IN,
            //TODO: need to review if gift card can buy in table service
            systemOrderChannelID: OrderChannel.QuickService,
            customerID: ownerCustomerID ?? defaultCustomerID,
            paymentCurrencyID: currencyID!,
            payments: [payment],
            pinCode,
            pointRedeem,
            giftCardSaleID: Guid.empty,
        }

        const result = await paymentService.saleGiftCard(payload)
        setGiftCardSaleID(result.data.giftCardSaleID ?? '')
        return { ...result.data, saleID: result.data.giftCardSaleID! }
    }
    const saleDetail: OrderDetailModel = useMemo(() => {
        return {
            orderID: giftCardSaleID || MOCK_GIFT_CARD_ORDER_ID,
            orderLines: [],
            orderNo: '',
            serviceChargeAmount: 0,
            totalTaxAmount: totalTax.toNumber(),
            totalDueAmount: totalPrice.toNumber(),
            subtotal: 0,
            discountAmount: 0,
            taxExclusiveAmount: taxExclusive.toNumber(),
            taxInclusiveAmount: taxInclusive.toNumber(),
        }
    }, [giftCardSaleID, taxExclusive, taxInclusive, totalPrice, totalTax])

    const [, getGiftCardSaleByID] = useFetchApi(salesService.getGiftCardSale)
    const handleGetSaleDetails = useCallback(
        async (id: string) => {
            const res = await getGiftCardSaleByID(id)
            return { ...res, saleID: res?.giftCardSaleID! }
        },
        [getGiftCardSaleByID],
    )
    useEffect(() => {
        if (isDialogOpened) {
            getGiftCardProducts()
            setCartItems([])
            setOwnerCustomerID(orderCustomerID ?? defaultCustomerID)
        }
    }, [
        isDialogOpened,
        getGiftCardProducts,
        orderCustomerID,
        defaultCustomerID,
    ])
    return {
        loading: getGiftCardProductsStatus === FetchingStatus.LOADING,
        giftCardProducts: giftCardProducts ?? [],
        cartItems,
        ownerCustomerID,
        onSelectCustomer,
        addNewItem,
        increaseQuantity,
        removeItem,
        totalPrice,
        handleOnProcessingSale,
        totalTax,
        taxExclusive,
        taxInclusive,
        saleDetail,
        handleGetSaleDetails,
    }
}

export default useBuyGiftCard
